import { callBackend } from '@/plugins/firebase'
import { getMspWebEnv, isMspWebConfigured } from '@/shared/config/mspWebEnv'
import { loadMspSdk } from './mspSdkLoader'

let _component = null

export const multisafepayClient = {
  name: 'multisafepay',
  usesInternalSubmitButton: false,

  async mount(host, ctx) {
    if (!isMspWebConfigured()) {
      throw new Error('MSP is not configured. Set VITE_MSP_ENVIRONMENT in .env.')
    }

    const { environment } = getMspWebEnv()
    await loadMspSdk(environment)

    if (typeof window.MultiSafepay !== 'function') {
      throw new Error('MSP SDK loaded but window.MultiSafepay is not a function — check the CDN script.')
    }

    const { apiToken } = await callBackend('payments/msp/api-token', {})

    // MSP SDK expects amount in cents (integer)
    const amountCents = Math.round((ctx.amount || 0) * 100)

    _component = new window.MultiSafepay({
      env: environment,
      apiToken,
      order: {
        // MSP `/v1/connect/payments/methods` returns HTTP 401 "Clave API inválida"
        // when the currency is lowercase (e.g. "eur"). Upper-case it for the SDK.
        currency: (ctx.currency || 'EUR').toUpperCase(),
        amount: amountCents,
        customer: {
          locale: ctx.locale || 'en',
          country: ctx.country || 'ES',
        },
        payment_options: {
          settings: {
            connect: {
              group_cards: true,
              redirect_mode: 'modal',
            },
          },
        },
      },
    })

    // MSP SDK requires a CSS selector string, not an Element reference.
    // The mount host always carries an id (e.g. #MultiSafepayPayment).
    const containerSelector = host?.id ? `#${host.id}` : host
    // Dropin in "existing button" mode: omit `onSubmit` so the SDK skips its
    // internal Pay button; the wizard footer button drives submit() instead.
    // (See MSP docs page payment-component-multiple, "Init with Existing Button".)
    _component.init('payment', { container: containerSelector, gateway: 'CREDITCARD' })
  },

  async submit(ctx) {
    if (!_component) {
      return { status: 'failed', details: { code: 'MSP_NOT_MOUNTED' } }
    }
    if (_component.hasErrors()) {
      const errors = _component.getErrors ? _component.getErrors() : null
      return { status: 'failed', details: { code: 'MSP_FORM_INVALID', errors } }
    }

    const paymentData = _component.getPaymentData()
    const gateway = paymentData?.payment_method

    let response
    try {
      response = await callBackend('payments/create-order', {
        provider: 'multisafepay',
        projectId: ctx.projectId,
        organizationId: ctx.organizationId,
        currency: ctx.currency,
        redirectUrl: ctx.returnUrl,
        cancelUrl: ctx.cancelUrl,
        paymentData,
        gateway,
        userId: ctx.userId,
      })
    } catch (err) {
      // axios rejects with `err.message` like "Request failed with status code 500".
      // The backend AppError JSON ({ code, message: "MSP error 1024: declined", ... })
      // lives at `err.response.data`. Prefer that over the axios message.
      const backendBody = err?.response?.data
      const backendMessage = backendBody?.message || err?.message || ''
      const mspMatch = /MSP error (\d+)\s*:\s*(.+)/i.exec(backendMessage)
      return {
        status: 'failed',
        details: {
          code: backendBody?.code || 'MSP_BACKEND_ERROR',
          mspCode: mspMatch ? Number(mspMatch[1]) : null,
          mspMessage: mspMatch ? mspMatch[2].trim() : null,
          rawMessage: backendMessage,
        },
      }
    }

    // Free-order fast-path: backend already completed the charge (e.g. 100% coupon
    // dropped total to 0). No MSP redirect, no SDK confirmation; the host handles
    // navigation to the success URL.
    if (response?.paymentStatus === 'complete') {
      return { status: 'succeeded', orderId: response.orderId }
    }

    if (!response?.paymentUrl) {
      return { status: 'failed', details: { code: 'MSP_NO_PAYMENT_URL', response } }
    }

    // Backend may return either an MSP-hosted checkout URL (when 3DS / extra
    // auth is needed) or our own success URL (when paymentStatus === 'succeeded'
    // server-side). A plain navigation works for both; `init('redirection')`
    // would only handle the first case AND requires the full MSP order payload.
    window.location.href = response.paymentUrl
    return { status: 'redirecting', orderId: response.orderId }
  },

  async destroy() {
    _component = null
  },
}
